import { useChatBot } from '@root/context/ChatBotContext';
import { useTheme } from '@root/context/ThemeContext';
import { copyTrackingService } from '@root/services/copyTrackingService';

import { Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import './style.scss';

const ViperAIIcon = ({ width = 20, height = 20 }) => (
  <svg width={width} height={height} viewBox="0 0 17 23" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.43085 8.84685C9.58667 8.3794 10.2468 8.3794 10.4026 8.84685L11.3162 11.5906C11.5173 12.1938 11.8562 12.7418 12.3059 13.1913C12.7556 13.6407 13.3038 13.9792 13.907 14.18L16.6494 15.0937C17.1169 15.2495 17.1169 15.9096 16.6494 16.0654L13.9056 16.9791C13.3025 17.1802 12.7544 17.519 12.305 17.9687C11.8555 18.4184 11.517 18.9666 11.3162 19.5699L10.4026 22.3122C10.369 22.4146 10.3039 22.5037 10.2167 22.5669C10.1294 22.6301 10.0244 22.6641 9.91672 22.6641C9.80899 22.6641 9.70401 22.6301 9.61676 22.5669C9.52951 22.5037 9.46445 22.4146 9.43085 22.3122L8.5172 19.5684C8.31624 18.9654 7.97762 18.4175 7.52818 17.9681C7.07874 17.5186 6.53082 17.18 5.92781 16.9791L3.18402 16.0654C3.08166 16.0318 2.99253 15.9667 2.92934 15.8795C2.86614 15.7922 2.83212 15.6873 2.83212 15.5795C2.83212 15.4718 2.86614 15.3668 2.92934 15.2796C2.99253 15.1923 3.08166 15.1273 3.18402 15.0937L5.92781 14.18C6.53082 13.9791 7.07874 13.6404 7.52818 13.191C7.97762 12.7416 8.31624 12.1936 8.5172 11.5906L9.43085 8.84685Z"
      fill="#00DBDE"
    />
  </svg>
);

const TextSelectionViperAI = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [copied, setCopied] = useState(false);
  const { isDarkMode } = useTheme();
  const { openChatBot } = useChatBot();
  const buttonRef = useRef(null);
  const isSelectingRef = useRef(false);
  const [mouseIsMoving, setMouseIsMoving] = useState(false);
  const stopTimerRef = useRef(null);
  const mouseDownRef = useRef(false);
  const mouseMovedRef = useRef(false);

  // --- Handle text selection ---
  useEffect(() => {
    const handleMouseDown = () => {
      mouseDownRef.current = true;
      mouseMovedRef.current = false;
    };

    const handleMouseMove = () => {
      // Track if mouse moved while button was pressed
      if (mouseDownRef.current) {
        mouseMovedRef.current = true;
      }
    };

    const handleTextSelection = e => {
      // Only proceed if mouse was pressed and moved (actual drag selection)
      if (!mouseDownRef.current || !mouseMovedRef.current) {
        mouseDownRef.current = false;
        mouseMovedRef.current = false;
        return;
      }

      mouseDownRef.current = false;
      const selection = window.getSelection();
      const text = selection.toString().trim();

      if (isVisible && text === selectedText) return;

      if (text && text.length > 3) {
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const isInputField =
          container.nodeType === Node.ELEMENT_NODE
            ? container.closest('input, textarea, [contenteditable]')
            : container.parentElement?.closest('input, textarea, [contenteditable]');
        if (isInputField) {
          setIsVisible(false);
          mouseMovedRef.current = false;
          return;
        }

        const newPosition = {
          top: Math.max(10, e.clientY - 30),
          left: Math.max(10, Math.min(e.clientX + 10, window.innerWidth - 80)),
        };

        setPosition(newPosition);
        setSelectedText(text);
        isSelectingRef.current = true;
        setIsVisible(true);

        // Allow mouse tracking AFTER selection is done
        setTimeout(() => {
          isSelectingRef.current = false;
        }, 250);
      } else {
        setIsVisible(false);
      }

      mouseMovedRef.current = false;
    };

    const handleMouseMovingAndStopOnce = e => {
      // Ignore while text selection is happening or if mouse button is pressed
      if (isSelectingRef.current || !isVisible || mouseDownRef.current) return;

      if (!mouseIsMoving) {
        setMouseIsMoving(true);
      }

      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);

      stopTimerRef.current = setTimeout(() => {
        setMouseIsMoving(false);
        stopTimerRef.current = null;

        // Check cursor position relative to popup
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;

        const { clientX: x, clientY: y } = e;

        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          // Cursor is inside, keep visible
        } else {
          // Cursor moved outside, hide the button
          setIsVisible(false);
        }
      }, 120);
    };

    const handleClickOutside = event => {
      if (isSelectingRef.current) return;
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsVisible(false);
        mouseDownRef.current = false;
        mouseMovedRef.current = false;
      }
    };

    const handleScroll = () => {
      setIsVisible(false);
      mouseDownRef.current = false;
      mouseMovedRef.current = false;
    };

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setIsVisible(false);
        mouseDownRef.current = false;
        mouseMovedRef.current = false;
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleTextSelection);
    document.addEventListener('mousemove', handleMouseMovingAndStopOnce);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('mousemove', handleMouseMovingAndStopOnce);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, mouseIsMoving, selectedText]);

  // --- Copy / Chat actions ---
  const handleViperAIClick = () => {
    openChatBot(selectedText);
    setIsVisible(false);
    isSelectingRef.current = false;
  };

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(selectedText);
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const sourceElement =
        range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;
      copyTrackingService.trackCopy(selectedText, sourceElement);
      setCopied(true);
      toast.info('Copied to clipboard');
      setTimeout(() => {
        setCopied(false);
        setIsVisible(false);
        isSelectingRef.current = false;
      }, 1500);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = selectedText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      copyTrackingService.trackCopy(selectedText);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setIsVisible(false);
        isSelectingRef.current = false;
      }, 1500);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      ref={buttonRef}
      className={`text-selection-viper-ai ${isDarkMode ? 'dark' : ''}`}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
      }}>
      <div className="action-menu">
        <button className="action-button viper-button" onClick={handleViperAIClick} title="Ask ViperAI about this text">
          <ViperAIIcon width={20} height={20} />
        </button>

        <button
          className="action-button copy-button"
          onClick={handleCopyClick}
          title={copied ? 'Copied!' : 'Copy text'}>
          <Copy size={16} />
        </button>
      </div>
    </div>
  );
};

export default TextSelectionViperAI;
